import React, { useContext, useEffect, useState } from 'react'
import { Badge, CompanyTag } from '@/components/ui/badge'
import { Page } from '@progress/kendo-react-dropdowns'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridPageChangeEvent,
} from '@progress/kendo-react-grid'
import { NameDto } from 'api/Common/Types/NameDto'

import { PermissionsEnum } from '../../../api/Identity/Types/PermissionsEnum'
import {
  BonusCampaignCountryDto,
  BonusCampaignCountryFormValuesEdit,
  BonusCampaignsDto,
} from '../../../api/Wallet/Types/MarketingBonusCampaignDto'
import AddButton from '../../../components/AddButton/AddButton'
import { Modal } from '../../../components/CustomModal'
import MoreButton from '../../../components/MoreButton/MoreButton'
import { Spinner } from '../../../components/Spinner'
import { successToast, ToastContext } from '../../../components/Toast/Toast'
import { calculatePageIndex } from '../../../features/DataGrid/helpers'
import { useAppDispatch, useAppSelector } from '../../../store'
import { pageableSettings } from '../../../store/grid/grid.slice'
import {
  fetchCountriesAction,
  fetchTickmillCompaniesAction,
} from '../../../store/identity/identity.actions'
import { useApiClient } from '../../../utils/api/ApiClient'
import { IdentityApiClient } from '../../../utils/api/identityApi'
import { useCallbackWithForceRefresh } from '../../../utils/api/useCallbackWithForceRefresh'
import { useFetchOneWithFetchingUpdate } from '../../../utils/api/useFetchOneWithFetchingUpdate'
import { WalletsApiClient } from '../../../utils/api/walletsApiClient'
import { AuthSessionContext } from '../../../utils/AuthContext'
import { isEmpty } from '../../../utils/is-empty'
import { getPaginationInitialState } from '../../../utils/paginationInitialState'
import { hasPermission } from '../../../utils/permissionsValidator'
import stringComparator from '../../../utils/stringComparator'
import { CampaignTab } from '../CampaignSlug'
import {
  AddBonusCampaignCountryForm,
  AddBonusCountryFormValues,
} from './AddBonusCampaignCountryForm'
import { BonusCampaignNavigation } from './BonusCampaignNavigation'
import { EditBonusCampaignCountryForm } from './EditBonusCampaignCountryForm'

import styles from './BonusCampaignDetailsCountryPage.module.scss'

interface OuterProps {
  data: BonusCampaignsDto

  afterSubmit(): void
}

const BonusCampaignDetailsCountryPage: React.FC<OuterProps> = (props) => {
  const { data, afterSubmit } = props

  const dispatch = useAppDispatch()

  const walletsApiClient = useApiClient(WalletsApiClient)
  const identityApiClient = useApiClient(IdentityApiClient)
  const [auth] = useContext(AuthSessionContext)
  const setToast = useContext(ToastContext)
  const permissions = auth?.permissions || []

  const tickmillCompanies = useAppSelector((state) => state.identity.tickmillCompanies)
  const countriesList = useAppSelector((state) => state.identity.countries)

  const [countries, setCountries] = useState<BonusCampaignCountryDto[]>([])
  const [isAddCountryModalOpen, setIsAddCountryModalOpen] = useState(false)
  const [showEditCountryModal, setShowEditCountryModal] = useState<BonusCampaignCountryDto | null>(
    null
  )
  const [paginationState, setPaginationState] = useState<Page>(getPaginationInitialState())
  const bonusCampaignId = data.id

  const { callback, forceRefresh } = useCallbackWithForceRefresh(() => {
    return walletsApiClient.getBonusCampaignCountries(bonusCampaignId, {
      pageIndex: calculatePageIndex(paginationState.skip, paginationState.take),
      pageSize: paginationState.take,
    })
  }, [])

  const { data: countriesData, isLoading } = useFetchOneWithFetchingUpdate(callback)

  useEffect(() => {
    if (countriesData) {
      setCountries(countriesData.items)
    }
  }, [countriesData]) // eslint-disable-next-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isEmpty(tickmillCompanies)) dispatch(fetchTickmillCompaniesAction(identityApiClient))
    if (isEmpty(countriesList)) {
      dispatch(fetchCountriesAction(identityApiClient))
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddSubmit = async (values: AddBonusCountryFormValues) => {
    await walletsApiClient.addBonusCampaignCountry(bonusCampaignId, values)
    setToast(successToast('Country added successfully.'))
    setIsAddCountryModalOpen(false)
    forceRefresh()
    afterSubmit()
  }

  const handleEditSubmit = async (values: BonusCampaignCountryFormValuesEdit) => {
    if (bonusCampaignId && values.countryIds.length > 0) {
      await walletsApiClient.updateBonusCampaignCountry(
        bonusCampaignId,
        values.bonusCampaignCountryOptionId,
        values
      )
      setToast(successToast('Country edited successfully.'))
      setShowEditCountryModal(null)
      forceRefresh()
      afterSubmit()
    }
  }
  const handlePageChange = (event: GridPageChangeEvent) => {
    setPaginationState(event.page)
    forceRefresh()
  }

  if (isLoading) {
    return <Spinner />
  }

  return (
    <>
      {isAddCountryModalOpen && (
        <AddBonusCampaignCountryForm
          isOpen={isAddCountryModalOpen}
          onClose={() => setIsAddCountryModalOpen(false)}
          existingCountryOptions={countriesData?.items}
          countries={countriesList}
          tickmillCompanies={tickmillCompanies}
          onSubmit={handleAddSubmit}
        />
      )}
      {showEditCountryModal && (
        <Modal visible={!!showEditCountryModal} onClose={() => setShowEditCountryModal(null)}>
          <EditBonusCampaignCountryForm
            existingCountryOptions={countriesData?.items}
            tickmillCompanies={tickmillCompanies}
            initialValues={{
              bonusCampaignCountryOptionId: showEditCountryModal.bonusCampaignCountryOptionId,
              tickmillCompanyId: showEditCountryModal.tickmillCompany.id,
              minimumAmount: showEditCountryModal.minimumAmount,
              isActive: showEditCountryModal.isActive,
              countryIds: showEditCountryModal.countries.map((c) => c.id),
            }}
            onSubmit={handleEditSubmit}
          />
        </Modal>
      )}
      {data && <BonusCampaignNavigation data={data} tab={CampaignTab.PARTICIPATING_COUNTRIES} />}
      <AddButton
        text='Add Country Option'
        disabled={!hasPermission(permissions, PermissionsEnum.CanEditBonusCampaigns)}
        onClick={() => setIsAddCountryModalOpen(true)}
      />
      <Grid
        data={countries.sort((a, b) =>
          stringComparator(a.tickmillCompany.name, b.tickmillCompany.name)
        )}
        take={paginationState.take}
        skip={paginationState.skip}
        total={countriesData?.totalResults || 0}
        onPageChange={handlePageChange}
        pageable={pageableSettings}
      >
        <Column
          field='Countries'
          title='Countries'
          cell={(gridCellProps: GridCellProps) => {
            const countries = gridCellProps.dataItem.countries
            return (
              <td>
                {countries && countries.length > 0 && (
                  <div className={styles.countriesList}>
                    {countries.map((c: NameDto) => (
                      <Badge key={c.id} variant='neutral'>
                        {c.name}
                      </Badge>
                    ))}
                  </div>
                )}
              </td>
            )
          }}
        />
        <Column
          field='minimumAmount'
          cell={(gridCellProps) => (
            <td className='table-columns__number'>
              {gridCellProps.dataItem.minimumAmount.toFixed(2)}
            </td>
          )}
          title='Minimum Deposit'
        />
        <Column
          field='tickmillCompany.name'
          title='Entity'
          cell={(gridCellProps: GridCellProps) => (
            <td>
              <CompanyTag id={gridCellProps.dataItem.tickmillCompany.id}>
                {gridCellProps.dataItem.tickmillCompany.name}
              </CompanyTag>
            </td>
          )}
        />
        <Column
          cell={(gridCellProps) => (
            <td align='center'>
              <input type='checkbox' checked={gridCellProps.dataItem.isActive} disabled />
            </td>
          )}
          title='Status'
        />
        <Column
          cell={(gridCellProps: GridCellProps) => (
            <td>
              <MoreButton
                items={[
                  {
                    name: 'Edit',
                    disabled: !hasPermission(permissions, PermissionsEnum.CanEditBonusCampaigns),
                    onClick: () => setShowEditCountryModal(gridCellProps.dataItem),
                  },
                ]}
              />
            </td>
          )}
          title=''
        />
      </Grid>
    </>
  )
}

export default BonusCampaignDetailsCountryPage
